import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Marker } from '@react-google-maps/api';
import SafeGoogleMap from './components/SafeGoogleMap';
import { useRobinhoodGoogleMaps } from './hooks/useRobinhoodGoogleMaps';
import MapsUnavailable from './components/MapsUnavailable';
import { getGoogleMapsKeyIssue } from './api/mapsConfig';
import { fetchPermits } from './api/govopsPermitService';
import { formatGovOpsError, isGovOpsAuthError } from './api/govopsErrors';
import { formatStatus, formatTimestamp } from './constants/workflowLabels';
import {
  permitsWithValidCoordinates,
  isGoogleMapsReady,
} from './utils/mapCoordinates';
import { getPermitMarkerIcon } from './utils/mapMarkers';
import {
  emptyQuickIntakeForm,
  INTAKE_MODES,
  INTAKE_SOURCES,
} from './utils/permitIntake';
import { usePermitIntake } from './hooks/usePermitIntake';
import PermitQuickIntakeForm from './components/PermitQuickIntakeForm';
import PageShell from './components/PageShell';
import PilotOutreachBanner from './components/PilotOutreachBanner';
import PilotDemoNavLinks from './components/PilotDemoNavLinks';

const MAP_CENTER = { lat: 32.7157, lng: -117.1611 };
const MAP_STYLE = { width: '100%', height: '420px' };

const STATUS_CLASS = {
  intake: 'rhs-badge--intake',
  under_review: 'rhs-badge--review',
  notice_sent: 'rhs-badge--notice',
  violation_open: 'rhs-badge--violation',
  payment_pending: 'rhs-badge--payment',
  resolved: 'rhs-badge--resolved',
  closed: 'rhs-badge--closed',
  appealed: 'rhs-badge--appeal',
};

function shortId(id) {
  if (!id) return '—';
  const s = String(id);
  return s.length > 12 ? `${s.slice(0, 8)}…` : s;
}

function computeKpis(permits) {
  const onMap = permitsWithValidCoordinates(permits).length;
  const active = permits.filter((p) =>
    ['intake', 'under_review', 'notice_sent', 'violation_open', 'payment_pending', 'appealed'].includes(
      p.status
    )
  ).length;
  const done = permits.filter((p) =>
    ['resolved', 'closed'].includes(p.status)
  ).length;
  return { total: permits.length, onMap, active, done };
}

const DashboardMap = ({ permits, onMapReady, onSelectPermit }) => {
  const mapped = useMemo(
    () => permitsWithValidCoordinates(permits),
    [permits]
  );

  const handleLoad = useCallback(
    (mapInstance) => {
      onMapReady(mapInstance);
      if (!isGoogleMapsReady() || !mapped.length) return;
      const bounds = new window.google.maps.LatLngBounds();
      mapped.forEach(({ position }) => {
        bounds.extend(new window.google.maps.LatLng(position.lat, position.lng));
      });
      mapInstance.fitBounds(bounds);
    },
    [mapped, onMapReady]
  );

  return (
    <SafeGoogleMap
      mapContainerStyle={MAP_STYLE}
      center={MAP_CENTER}
      zoom={11}
      onLoad={handleLoad}
      options={{ streetViewControl: false, mapTypeControl: false }}
    >
      {mapped.map(({ permit, position }) => (
        <Marker
          key={permit.id}
          position={position}
          title={permit.type || permit.description || 'Permit'}
          icon={getPermitMarkerIcon(permit.status)}
          onClick={() => onSelectPermit?.(permit)}
        />
      ))}
    </SafeGoogleMap>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(emptyQuickIntakeForm);
  const [permits, setPermits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [, setMap] = useState(null);

  const { isLoaded, loadError } = useRobinhoodGoogleMaps();
  const keyIssue = getGoogleMapsKeyIssue();
  const mapsReady = !keyIssue && !loadError && isLoaded && isGoogleMapsReady();

  const loadPermits = useCallback(async () => {
    try {
      const permitsData = await fetchPermits();
      setPermits(permitsData);
      setError('');
    } catch (err) {
      if (!isGovOpsAuthError(err)) console.error(err);
      setError(formatGovOpsError(err) || 'Failed to load permits');
    } finally {
      setLoading(false);
    }
  }, []);

  const {
    submit: submitIntake,
    submitting,
    error: submitError,
    success,
  } = usePermitIntake({
    source: INTAKE_SOURCES.DASHBOARD,
    mode: INTAKE_MODES.QUICK,
    onSuccess: async () => {
      setFormData(emptyQuickIntakeForm());
      await loadPermits();
    },
  });

  useEffect(() => {
    loadPermits();
  }, [loadPermits]);

  const kpis = useMemo(() => computeKpis(permits), [permits]);
  const recentPermits = useMemo(() => permits.slice(0, 6), [permits]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await submitIntake(formData);
    } catch (err) {
      if (!isGovOpsAuthError(err)) console.error(err);
    }
  };

  if (loading) {
    return (
      <PageShell
        title="Dashboard"
        subtitle="Submit permits and monitor false-alarm activity for your organization."
      >
        <div className="rhs-loading" role="status">
          <span className="rhs-spinner" aria-hidden="true" />
          Loading dashboard…
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Dashboard"
      subtitle="Submit permits and monitor false-alarm activity for your organization."
      className="rhs-dashboard"
    >
      <PilotOutreachBanner />

      <nav className="rhs-quick-nav" aria-label="Quick actions">
        <PilotDemoNavLinks />
        <Link to="/map" className="rhs-quick-nav-item">
          View full map
        </Link>
        <Link to="/permit-history" className="rhs-quick-nav-item">
          Permit history
        </Link>
        <Link to="/permit/submit" className="rhs-quick-nav-item">
          Submit with address search
        </Link>
        <Link to="/contact-support" className="rhs-quick-nav-item">
          Contact support
        </Link>
      </nav>

      <div className="rhs-kpi-grid" aria-label="Summary">
        <div className="rhs-kpi-card">
          <div className="rhs-kpi-value">{kpis.total}</div>
          <div className="rhs-kpi-label">Total permits</div>
        </div>
        <div className="rhs-kpi-card">
          <div className="rhs-kpi-value">{kpis.onMap}</div>
          <div className="rhs-kpi-label">On map</div>
        </div>
        <div className="rhs-kpi-card">
          <div className="rhs-kpi-value">{kpis.active}</div>
          <div className="rhs-kpi-label">In progress</div>
        </div>
        <div className="rhs-kpi-card">
          <div className="rhs-kpi-value">{kpis.done}</div>
          <div className="rhs-kpi-label">Resolved / closed</div>
        </div>
      </div>

      {error && (
        <div className="rhs-error-panel" role="alert">
          <strong>Notice</strong>
          <p className="rhs-muted">{error}</p>
        </div>
      )}
      {submitError && (
        <p className="rhs-error" role="alert">
          {submitError}
        </p>
      )}
      {success && (
        <p className="rhs-success-banner" role="status">
          {success}
        </p>
      )}

      <div className="rhs-dashboard-grid">
        <section className="rhs-card" aria-labelledby="submit-permit-heading">
          <h2 id="submit-permit-heading" className="rhs-card-title">
            Submit new permit
          </h2>
          <p className="rhs-muted rhs-card-lead">
            Enter coordinates manually. For address search, use{' '}
            <Link to="/permit/submit">map submit</Link>.
          </p>
          <PermitQuickIntakeForm
            idPrefix="dash"
            formData={formData}
            onChange={setFormData}
            onSubmit={handleSubmit}
            submitting={submitting}
            error={submitError}
            success={success}
            showOptionalFields
          />
        </section>

        <section className="rhs-card" aria-labelledby="map-overview-heading">
          <h2 id="map-overview-heading" className="rhs-card-title">
            Permit locations
          </h2>
          {keyIssue && <MapsUnavailable compact showActions={false} />}
          {!keyIssue && loadError && (
            <MapsUnavailable loadError={loadError} compact showActions={false} />
          )}
          {!keyIssue && !loadError && !isLoaded && (
            <p className="rhs-muted">Loading map…</p>
          )}
          {!keyIssue && !loadError && isLoaded && !isGoogleMapsReady() && (
            <MapsUnavailable compact showActions={false} />
          )}
          {mapsReady && (
            <div className="rhs-map-frame">
              <DashboardMap
                permits={permits}
                onMapReady={setMap}
                onSelectPermit={(permit) =>
                  navigate('/map', { state: { permitId: permit.id } })
                }
              />
            </div>
          )}
          {!mapsReady && kpis.onMap > 0 && (
            <p className="rhs-muted rhs-card-lead">
              {kpis.onMap} permit(s) have coordinates — open{' '}
              <Link to="/map">full map</Link> when Maps is configured.
            </p>
          )}
        </section>
      </div>

      <section className="rhs-card rhs-card--flush" aria-labelledby="recent-permits-heading">
        <div className="rhs-card-header">
          <h2 id="recent-permits-heading" className="rhs-card-title">
            Recent permits
          </h2>
          <Link to="/permit-history" className="rhs-table-link">
            View all →
          </Link>
        </div>

        {recentPermits.length === 0 ? (
          <div className="rhs-empty-state rhs-empty-state--compact">
            <p className="rhs-muted">No permits yet. Submit your first permit above.</p>
          </div>
        ) : (
          <div className="rhs-table-wrap">
            <table className="rhs-table rhs-table--history">
              <thead>
                <tr>
                  <th scope="col">Permit</th>
                  <th scope="col">Type</th>
                  <th scope="col">Status</th>
                  <th scope="col">Submitted</th>
                  <th scope="col">
                    <span className="rhs-sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {recentPermits.map((permit) => {
                  const statusKey = permit.status || 'intake';
                  const badgeClass =
                    STATUS_CLASS[statusKey] || 'rhs-badge--default';
                  return (
                    <tr key={permit.id}>
                      <td data-label="Permit">
                        <span className="rhs-permit-id" title={permit.id}>
                          {shortId(permit.id)}
                        </span>
                      </td>
                      <td data-label="Type">{permit.type || '—'}</td>
                      <td data-label="Status">
                        <span className={`rhs-badge ${badgeClass}`}>
                          {formatStatus(statusKey)}
                        </span>
                      </td>
                      <td data-label="Submitted">
                        {formatTimestamp(permit.createdAt || permit.date)}
                      </td>
                      <td data-label="Actions">
                        <Link
                          to={`/permit-details/${permit.id}`}
                          className="rhs-table-link"
                        >
                          Details
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </PageShell>
  );
};

export default DashboardPage;
